/**
 * ScheduleConnector - Schedule step of the Add Connector Wizard (#273)
 *
 * Lets the user pick a run cadence at creation time, instead of only on the
 * detail-page Schedule tab. Reuses the detail page's building blocks:
 * ScheduleFrequencyForm (Off / Hourly / Daily / Weekly / Monthly + time
 * controls), the shared timezone list, and SchedulePreview.
 *
 * Scheduling defaults to Off ("Run manually") and is skippable -- the wizard
 * keeps Continue enabled on this step. The step is only rendered for connector
 * types whose schema reports supports_scheduling; the wizard controller drops
 * it from the step list otherwise.
 */
import { useState } from 'react';
import { Hand, Info } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SchedulePreview } from '@/components/connectors/schedule/SchedulePreview';
import { TIMEZONES } from '@/components/connectors/schedule/timezones';
import { DEFAULT_SCHEDULE_TIMEZONE } from '@/constants/timezone';
import {
  ScheduleFrequencyForm,
  type FormState,
  formStateToCron,
  presetToFormState,
} from '@/components/connectors/detail/ScheduleFrequencyForm';
import { cronExpressionToPreset } from '@/components/connectors/detail/cronExpressionToPreset';
import type { ConnectorType } from '@/types/connectors';
import type { ConnectorConfigFormData } from './ConfigureConnector';

interface ScheduleConnectorProps {
  connectorType: ConnectorType;
  formData: ConnectorConfigFormData;
  onChange: (data: ConnectorConfigFormData) => void;
}

/** Seed the structured form state from the wizard's persisted schedule fields. */
function seedFormState(formData: ConnectorConfigFormData): FormState {
  if (!formData.schedule_enabled) return { frequency: 'off' };
  return presetToFormState(cronExpressionToPreset(formData.schedule_cron ?? null)) ?? { frequency: 'off' };
}

export function ScheduleConnector({ connectorType, formData, onChange }: ScheduleConnectorProps) {
  const [value, setValue] = useState<FormState>(() => seedFormState(formData));
  const [timezone, setTimezone] = useState<string>(formData.schedule_timezone ?? DEFAULT_SCHEDULE_TIMEZONE);

  const isOff = !value || value.frequency === 'off';
  const cron = formStateToCron(value);

  const handleFrequency = (next: FormState) => {
    setValue(next);
    const nextCron = formStateToCron(next);
    onChange({
      ...formData,
      schedule_enabled: !!next && next.frequency !== 'off',
      schedule_cron: nextCron ?? undefined,
      schedule_timezone: timezone,
    });
  };

  const handleTimezone = (tz: string) => {
    setTimezone(tz);
    onChange({ ...formData, schedule_timezone: tz });
  };

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <Label>Frequency</Label>
        <ScheduleFrequencyForm value={value} onChange={handleFrequency} />
      </div>

      {isOff ? (
        <div className="flex max-w-xl items-start gap-3 rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          <Hand className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p>
            <span className="font-medium text-foreground">Run manually.</span> This connector
            won&rsquo;t run on a schedule. You can trigger it by hand from its detail page, or set a
            schedule later on the Schedule tab.
          </p>
        </div>
      ) : (
        <>
          <div className="grid max-w-md grid-cols-[120px_1fr] items-center gap-2">
            <Label htmlFor="wizard-timezone-select">Timezone</Label>
            <Select value={timezone} onValueChange={handleTimezone}>
              <SelectTrigger id="wizard-timezone-select" aria-label="Timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(([v, label]) => (
                  <SelectItem key={v} value={v}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {cron && (
            <div>
              <div className="mb-2 text-xs uppercase text-muted-foreground">Next runs</div>
              <SchedulePreview
                connectorType={connectorType}
                connectorName={formData.name}
                cronExpression={cron}
                timezone={timezone}
                count={5}
                className="max-w-xl"
              />
            </div>
          )}
        </>
      )}

      <p className="flex max-w-xl items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        You can change this anytime on the connector&rsquo;s Schedule tab after it&rsquo;s created.
      </p>
    </div>
  );
}
